import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import type { WoundResponse } from '../../services/api';
import AddWoundButton from './AddWoundButton';
import WoundsTable from './WoundsTable';
import WoundCareWorksheetModal from './WoundCareWorksheetModal';
import type { WoundImage } from './useWoundImages';

type WoundStatusFilter = 'active' | 'discontinued';

interface WoundsCardProps {
  patientId: string;
  wounds: WoundResponse[];
  woundImages?: Record<string, WoundImage[]>;
  downloadOnlyCounts?: Record<string, number>;
  isLoading: boolean;
  error: string | null;
  canAdd?: boolean;
  canEdit?: boolean;
  visitId?: string;
  /** Visit picker for write context, rendered next to Export / Add Wound. */
  visitSelector?: React.ReactNode;
  /**
   * Patient-chart Wounds tab: Active / Discontinued tabs, Active first.
   * Visit notes omit this so the table stays flat. Radix unmounts inactive
   * TabsContent, which would hide discontinued rows from Live Scrubber jumps
   * (EMR-931: healed_pressure_injury / healed_venous_ulcer /
   * healed_surgical_wound put discontinued ids in matched_ids).
   */
  splitByStatus?: boolean;
  onDocumentCreated?: () => void;
  onRetry: () => void;
  onAddWound: () => void;
  onEditWound: (wound: WoundResponse) => void;
  onDeleteWound: (wound: WoundResponse) => void;
  onDiscontinueWound: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
}

function WoundStatusPanel({
  rows,
  emptyMessage,
  woundImages,
  downloadOnlyCounts,
  canEdit,
  canDiscontinue,
  onEditWound,
  onDeleteWound,
  onDiscontinueWound,
  onOpenAssessments,
}: {
  rows: WoundResponse[];
  emptyMessage: string;
  woundImages?: Record<string, WoundImage[]>;
  downloadOnlyCounts?: Record<string, number>;
  canEdit: boolean;
  canDiscontinue: boolean;
  onEditWound: (wound: WoundResponse) => void;
  onDeleteWound: (wound: WoundResponse) => void;
  onDiscontinueWound: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
}) {
  if (rows.length === 0) {
    return (
      <p className='text-sm text-muted-foreground text-center py-8'>
        {emptyMessage}
      </p>
    );
  }

  return (
    <WoundsTable
      wounds={rows}
      woundImages={woundImages}
      downloadOnlyCounts={downloadOnlyCounts}
      canEdit={canEdit}
      canDiscontinue={canDiscontinue}
      onEdit={onEditWound}
      onDelete={onDeleteWound}
      onDiscontinue={onDiscontinueWound}
      onOpenAssessments={onOpenAssessments}
    />
  );
}

const WoundsCard: React.FC<WoundsCardProps> = ({
  patientId,
  wounds,
  woundImages,
  downloadOnlyCounts,
  isLoading,
  error,
  canAdd = false,
  canEdit = true,
  visitId,
  visitSelector,
  splitByStatus = false,
  onDocumentCreated,
  onRetry,
  onAddWound,
  onEditWound,
  onDeleteWound,
  onDiscontinueWound,
  onOpenAssessments,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Active is the default view — discontinued wounds stay one click away, the
  // same split Kinnser uses (Active Wounds, then Discontinued). Filtering lives
  // here so WoundsTable can keep sorting a single collection.
  const [statusFilter, setStatusFilter] = useState<WoundStatusFilter>('active');

  const { activeWounds, discontinuedWounds } = useMemo(() => {
    const active: WoundResponse[] = [];
    const discontinued: WoundResponse[] = [];
    for (const wound of wounds) {
      if (wound.discontinued_date) discontinued.push(wound);
      else active.push(wound);
    }
    return { activeWounds: active, discontinuedWounds: discontinued };
  }, [wounds]);

  const noWoundsMessage = canAdd
    ? 'No wounds recorded. Click "Add Wound" to document one.'
    : 'No wounds recorded for this patient.';

  const actionRow = (
    <div className='flex flex-wrap items-center justify-between gap-2'>
      <div className='flex w-full min-w-0 items-center sm:w-auto'>
        {visitSelector}
      </div>
      <div className='flex flex-wrap items-center gap-2'>
        {visitId && (
          <Button
            size='sm'
            variant='outline'
            onClick={() => setIsModalOpen(true)}
            disabled={isLoading}
          >
            <Download className='h-4 w-4 mr-1' />
            Export
          </Button>
        )}
        <AddWoundButton
          onClick={onAddWound}
          disabled={!canAdd}
          disabledReason={
            !canEdit
              ? 'Cannot edit because the visit is not in an editable state'
              : undefined
          }
        />
      </div>
    </div>
  );

  const panelProps = {
    woundImages,
    downloadOnlyCounts,
    canEdit,
    canDiscontinue: canAdd,
    onEditWound,
    onDeleteWound,
    onDiscontinueWound,
    onOpenAssessments,
  };

  const statusOrError = isLoading ? (
    <div className='flex justify-center py-8'>
      <div className='animate-spin rounded-full border-b-2 border-primary h-6 w-6' />
    </div>
  ) : error ? (
    <div className='text-center py-8 space-y-2'>
      <p className='text-sm text-destructive'>{error}</p>
      <button
        onClick={onRetry}
        className='text-xs text-muted-foreground underline hover:text-foreground'
      >
        Try again
      </button>
    </div>
  ) : null;

  const table = splitByStatus ? (
    <>
      <TabsContent value='active' className='mt-0'>
        <WoundStatusPanel
          rows={activeWounds}
          emptyMessage={
            wounds.length === 0 ? noWoundsMessage : 'No active wounds.'
          }
          {...panelProps}
        />
      </TabsContent>
      <TabsContent value='discontinued' className='mt-0'>
        <WoundStatusPanel
          rows={discontinuedWounds}
          emptyMessage={
            wounds.length === 0 ? noWoundsMessage : 'No discontinued wounds.'
          }
          {...panelProps}
        />
      </TabsContent>
    </>
  ) : (
    <WoundStatusPanel
      rows={wounds}
      emptyMessage={noWoundsMessage}
      {...panelProps}
    />
  );

  const body = (
    <>
      <div className='mb-3'>{actionRow}</div>
      {/* overflow-x-auto + min-w guard fixed-width columns on mobile (comment 1) */}
      <div className='overflow-x-auto'>
        <div className='min-w-[600px]'>{statusOrError ?? table}</div>
      </div>
    </>
  );

  return (
    <>
      {splitByStatus ? (
        <Tabs
          value={statusFilter}
          onValueChange={value => setStatusFilter(value as WoundStatusFilter)}
        >
          <div className='mb-3'>
            <TabsList className='overflow-x-auto'>
              <TabsTrigger value='active'>
                <span>Active ({activeWounds.length})</span>
              </TabsTrigger>
              <TabsTrigger value='discontinued'>
                <span>Discontinued ({discontinuedWounds.length})</span>
              </TabsTrigger>
            </TabsList>
          </div>
          {body}
        </Tabs>
      ) : (
        <Card>
          <CardContent className='pt-4'>{body}</CardContent>
        </Card>
      )}

      {visitId && (
        <WoundCareWorksheetModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          patientId={patientId}
          visitId={visitId}
          onDocumentCreated={onDocumentCreated}
        />
      )}
    </>
  );
};

export default WoundsCard;
